// Define the columns for the TableData component

import { useEffect, useRef } from "react";

import { Column, ColumnDef, Table } from "@tanstack/react-table";

import { ArrowSortIcon, SettingIcon } from "@/assets/icons";
import BadgeDataType from "@/components/ui/badge-data-type";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { cn } from "@/lib/utils";
import { TOOLTIP_TRIGGER_LENGTH } from "@/types/common";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDataRowValue,
  DatasetSchemaPayload
} from "@/types/dataset";
import { parseWithDayjs } from "@/utils/date";
import { formatRenderValue } from "@/utils/string";

import { MoreVertical } from "lucide-react";
import { twMerge } from "tailwind-merge";

// Define the data type for our table rows as a dynamic type
export type DatasetDetailRow = Record<string, DatasetDataRowValue>;

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  type: DataSchemaType;
};

export interface SortedHeaderProps extends DatasetAttributeSchema {
  column: Column<DatasetDetailRow>;
  title: string;
  published: boolean;
  mutatePatchDatasetSchema: (payload: DatasetSchemaPayload) => void;
  table?: Table<DatasetDetailRow>;
  withCheckbox?: boolean;
  isRowDatasetManagementEnabled?: boolean;
}

export function SortedHeader(props: SortedHeaderProps) {
  const {
    table,
    column,
    mutatePatchDatasetSchema,
    title,
    published,
    withCheckbox,
    isRowDatasetManagementEnabled
  } = props;
  const userPermissions = useGetUserPermission();
  const isEditDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.EDIT];
  const isDeleteDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.DELETE];
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate =
        table?.getIsSomeRowsSelected() || false;
    }
  }, [table]);

  const handleClickSetId = () => {
    mutatePatchDatasetSchema({
      id: props.id,
      attribute_name: props.attribute_name,
      data_type: props.data_type,
      is_nullable: props.is_nullable,
      is_spatial: props.is_spatial,
      position: props.position,
      is_reference_id: true,
      is_reference_geo: props.is_reference_geo,
      is_visible: props.is_visible,
      paired_key_attribute_id: props.paired_key_attribute_id
    });
  };

  return (
    <div className="flex gap-1 items-center">
      {withCheckbox &&
        isRowDatasetManagementEnabled &&
        isDeleteDataGranted &&
        !published && (
          <input
            ref={checkboxRef}
            type="checkbox"
            aria-label="checkbox-header"
            className="mr-2 w-3 h-3 accent-primary-teal"
            onChange={table?.getToggleAllRowsSelectedHandler()}
            checked={table?.getIsAllRowsSelected()}
          />
        )}
      <div className="flex flex-col items-start gap-1 h-full">
        <div className="flex flex-row items-center gap-2 w-full">
          <Button
            variant="ghost"
            className="flex flex-row items-center p-0 h-auto overflow-x-hidden"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            data-testid="sort-button"
            aria-label={`Sort by ${title}`}
          >
            <p
              className="text-txtBody text-xs font-semibold line-clamp-1 text-ellipsis"
              title={title}
            >
              {title.substring(0, 20)}
            </p>
            <ArrowSortIcon className="min-w-4" width={16} height={16} />
          </Button>
          {isEditDataGranted && (
            <Button
              variant="ghost"
              className="flex flex-row items-center p-0 h-auto"
              disabled={published}
              onClick={handleClickSetId}
              aria-label="Set as Reference ID"
            >
              <SettingIcon
                width={16}
                height={16}
                fill={props.is_reference_id ? "#CECECE" : "#616161"}
                className="ml-auto cursor-pointer"
              />
            </Button>
          )}
        </div>

        <div className="flex flex-row items-center mt-auto gap-2">
          <BadgeDataType type={props.data_type} />
          {props.is_reference_id && (
            <div className="text-white text-xs font-semibold py-0.5 px-2 bg-txtBody rounded-lg">
              id
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const generateColumns = ({
  columnsConfig,
  published,
  mutatePatchDatasetSchema,
  handleEditRow,
  handleDeleteRows,
  isRowDatasetManagementEnabled = false,
  isEditDataGranted = false,
  isDeleteDataGranted = false
}: {
  columnsConfig: DatasetAttributeSchema[];
  published: boolean;
  mutatePatchDatasetSchema: (payload: DatasetSchemaPayload) => void;
  handleEditRow: (row: DatasetDetailRow) => void;
  handleDeleteRows: (rowIndex: string) => void;
  isRowDatasetManagementEnabled?: boolean;
  isEditDataGranted?: boolean;
  isDeleteDataGranted?: boolean;
}): ExtendedColumnDef<DatasetDetailRow>[] => {
  const column = columnsConfig.map((col, index) => {
    const columnDef: ExtendedColumnDef<DatasetDetailRow> = {
      // When the column attribute_name has symbol on it (eg. Percentage Adm. Area),
      // The id of column will replace the symbol with underscore making it Percentage Adm_ Area
      // When getValue, the id and attribute_name will be mismatched
      // Thus making the value of column always empty
      // To fix this, we will set the accessorFn to get the value from attribute_name instead of id
      accessorFn: (row) => row[col.attribute_name],
      accessorKey: col.attribute_name,
      header: ({ table, column }) => {
        return (
          <SortedHeader
            {...col}
            published={published}
            table={table}
            column={column}
            title={col.attribute_name
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")}
            mutatePatchDatasetSchema={mutatePatchDatasetSchema}
            withCheckbox={index === 0}
            isRowDatasetManagementEnabled={isRowDatasetManagementEnabled}
          />
        );
      },
      cell: (info) => {
        const formattedValue = formatRenderValue(
          col.data_type,
          info.getValue() as DatasetDataRowValue,
          undefined,
          6,
          false,
          false,
          col?.data_label
        );
        const formattedValueString =
          typeof formattedValue === "number"
            ? String(formattedValue)
            : formattedValue;

        if (col.data_type === DataSchemaType.BOOL) {
          return (
            <div
              className={cn(
                "px-2 py-1 font-semibold rounded-lg text-xs inline-block",
                !!formattedValueString && "bg-white-smoke",
                info.getValue()
                  ? "bg-green-50 text-green-600 border border-green-200"
                  : "bg-red-50 text-red-600 border border-red-200"
              )}
            >
              {formattedValueString}
            </div>
          );
        }
        const isTooltipEnabled =
          formattedValueString.length > TOOLTIP_TRIGGER_LENGTH;

        return (
          <div
            className={cn(
              "text-txtBody text-xs h-full w-full flex items-center gap-1",
              !formattedValueString && "bg-white-smoke"
            )}
          >
            {index === 0 && isRowDatasetManagementEnabled && !published && (
              <input
                type="checkbox"
                aria-label="checkbox-column"
                className="mr-2 w-3 h-3 accent-primary-teal"
                checked={info.row.getIsSelected()}
                onChange={info.row.getToggleSelectedHandler()}
              />
            )}

            <p
              title={isTooltipEnabled ? formattedValueString : ""}
              className={twMerge(
                "line-clamp-2",
                isTooltipEnabled && "cursor-pointer"
              )}
            >
              {/* For performance sake, the displayed text is trimmed to 128 characters since it is already clamped by line-clamp-2 */}
              {formattedValueString.substring(0, 128)}
            </p>
          </div>
        );
      },
      type: col.data_type,
      enableSorting: true,
      size: 200
    };

    if (
      [
        DataSchemaType.DATE,
        DataSchemaType.DATE_TIME,
        DataSchemaType.TIME
      ].includes(col.data_type)
    ) {
      columnDef.sortingFn = (rowA, rowB, columnId) => {
        const dateA = parseWithDayjs(rowA.getValue(columnId));
        const dateB = parseWithDayjs(rowB.getValue(columnId));

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1; // or -1 depending on where you want nulls
        if (!dateB) return -1; // or 1

        return dateA.diff(dateB);
      };
    }

    return columnDef;
  });

  if (
    isRowDatasetManagementEnabled &&
    (isEditDataGranted || isDeleteDataGranted) &&
    !published
  ) {
    column.push({
      id: "action",
      accessorKey: "action",
      header: () => (
        <p className="text-txtBody text-xs font-semibold">Action</p>
      ),
      cell: (data) => (
        <div className="flex justify-center">
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="more actions"
              >
                <MoreVertical size={16} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="flex flex-col justify-start items-start w-24 p-0"
              side="left"
            >
              {isEditDataGranted && (
                <button
                  aria-label="edit action"
                  className="text-left text-xs hover:bg-neutral-50 hover:rounded-t-lg p-2 w-full cursor-pointer"
                  onClick={() => handleEditRow?.(data.row.original)}
                >
                  Edit
                </button>
              )}
              {isDeleteDataGranted && (
                <button
                  aria-label="delete action"
                  className="text-left text-xs hover:bg-neutral-50 hover:rounded-b-lg p-2 w-full cursor-pointer"
                  onClick={() => handleDeleteRows?.(String(data.row.index))}
                >
                  Delete
                </button>
              )}
            </PopoverContent>
          </Popover>
        </div>
      ),
      type: DataSchemaType.ID,
      maxSize: 62,
      enablePinning: true
    });
  }
  return column;
};
